// src/oura/components/docs/ReplyByControl.tsx
//
// A doc's reply-by date, and changing it for this doc only. The default is
// 5 working days after the doc was shared; staff can move it, or send it back
// to the default. The server refuses a date before today (reply_by_in_past),
// and so does this form, before it sends anything. A doc with nothing waiting
// on the client (approved, or nothing asked) shows its date with no form.
//
// ReplyByInline (drawer specs v1.1, A-C) is the same write as a "Change" text
// button in the doc drawer's "With the client" head: it opens a date field,
// Save, "Back to the default" (only when staff set the date) and Cancel.
// Save folds it again and hands the sheet its message ("Reply by Mon 5
// Oct."); the header's clock follows the next read. It never completes the doc.

import { useEffect, useId, useRef, useState } from "react";
import { CalendarIcon } from "lucide-react";
import { setDocumentReplyBy, type StaffDocument } from "@service/aeo/clientDocs";
import { useSnackbar } from "@hook/use-snackbar";
import { cn } from "../../lib/cn";
import { AREA_TEXT, LATE_TEXT, PillButton, TYPE, useEscapeLayer } from "@oura/ui";
import { docDate, docsFailureText, lateText } from "@oura/docs";
import { TAP_TARGET, myToday } from "./boardModel";

// 44px on a phone, where it is tapped; 36px beside a pointer. An input draws
// no pseudo-element, so TAP_TARGET cannot grow it: the field itself grows.
const FIELD = cn(
    TYPE.tb,
    "block h-11 w-full max-w-[12rem] rounded-xl border-0 bg-gray-50 px-3 text-gray-900 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-600 sm:h-9",
);

export function ReplyByControl({ doc }: { doc: StaffDocument }) {
    const snackbar = useSnackbar();
    const fieldId = useId();
    const errorId = useId();
    const today = myToday();
    const inputRef = useRef<HTMLInputElement>(null);
    const [draft, setDraft] = useState(doc.replyBy ?? "");
    const [busy, setBusy] = useState<"save" | "reset" | null>(null);
    const [error, setError] = useState<string | null>(null);

    // The date moved outside this form (a save, a reset, a newer read): the
    // field follows it without a remount, so focus stays where it is.
    const [seen, setSeen] = useState(doc.replyBy);
    if (seen !== doc.replyBy) {
        setSeen(doc.replyBy);
        setDraft(doc.replyBy ?? "");
        setError(null);
    }

    // Nothing waits on the client, so a new date could never make it late.
    const editable = doc.state !== "approved" && doc.state !== "nothing_asked";

    const when = docDate(doc.replyBy);
    // Late with 0 working days (the weekend straight after the reply-by) still says late; the date is on this line.
    const late = doc.late ? lateText(doc.lateWorkingDays) ?? "Late" : null;
    const source = doc.replyBySource === "staff" ? "Set by staff for this doc." : doc.replyBySource === "default" ? "5 working days after it was shared." : null;

    const send = async (value: string | null) => {
        if (busy) return;
        if (value !== null) {
            if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) {
                setError("Pick a date.");
                inputRef.current?.focus();
                return;
            }
            if (value < today) {
                setError("Pick today or a later date.");
                inputRef.current?.focus();
                return;
            }
        }
        setBusy(value === null ? "reset" : "save");
        setError(null);
        const r = await setDocumentReplyBy(doc.id, value);
        setBusy(null);
        if (!r.ok) {
            setError(r.reason === "network" ? "The date did not save. Try again." : docsFailureText(r, "this document"));
            // The busy button was disabled while it had focus.
            inputRef.current?.focus();
            return;
        }
        setDraft(r.data.replyBy ?? "");
        // Save goes disabled and "Back to the default" goes away once the
        // new date is read: focus stays in the sheet, on the field.
        inputRef.current?.focus();
        const d = docDate(r.data.replyBy);
        snackbar.showSuccess(value === null ? "Reply-by is back to the default." : d ? `Reply by ${d}.` : "Reply-by saved.");
    };

    return (
        <div>
            <p className={cn(TYPE.tb, "m-0 flex flex-wrap items-center gap-x-2 text-gray-900")}>
                <CalendarIcon aria-hidden size={14} className={AREA_TEXT.dates} />
                {when ? <span>Reply by {when}</span> : <span className="text-gray-500">No reply-by date</span>}
                {late && <span className={cn(TYPE.tc, LATE_TEXT)}>{late}</span>}
            </p>
            {source && <p className={cn(TYPE.tc, "m-0 mt-0.5 text-gray-500")}>{source}</p>}
            {editable && (
                <form
                    noValidate
                    className="mt-3"
                    onSubmit={(e) => {
                        e.preventDefault();
                        void send(draft);
                    }}
                >
                    <label htmlFor={fieldId} className={cn(TYPE.tb, "m-0 block font-medium text-gray-900")}>
                        Change it for this doc
                    </label>
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                        <input
                            ref={inputRef}
                            id={fieldId}
                            type="date"
                            min={today}
                            value={draft}
                            aria-invalid={error ? true : undefined}
                            aria-describedby={error ? errorId : undefined}
                            onChange={(e) => {
                                setDraft(e.target.value);
                                if (error) setError(null);
                            }}
                            className={FIELD}
                        />
                        <PillButton type="submit" size="sm" variant="primary" busy={busy === "save"} disabled={!draft || draft === doc.replyBy} className={TAP_TARGET}>
                            Save
                        </PillButton>
                        {doc.replyBySource === "staff" && (
                            <PillButton size="sm" variant="text" busy={busy === "reset"} onClick={() => void send(null)}>
                                Back to the default
                            </PillButton>
                        )}
                    </div>
                    {error && (
                        <p id={errorId} role="alert" className={cn(TYPE.tc, LATE_TEXT, "m-0 mt-2")}>
                            {error}
                        </p>
                    )}
                </form>
            )}
        </div>
    );
}

/**
 * The reply-by as a "Change" text button (A-C), for the head of the doc
 * drawer's "With the client" block. Opened, it is the date field, a small
 * blue Save, "Back to the default" and Cancel, all on one line that wraps.
 * Escape and Cancel fold it with nothing sent; focus goes back to "Change".
 */
export function ReplyByInline({
    doc,
    onSaved,
    onBusy,
}: {
    doc: StaffDocument;
    /** After a save: the sheet's foot says it ("Reply by Mon 5 Oct."). */
    onSaved: (text: string) => void;
    onBusy?: (busy: boolean) => void;
}) {
    const fieldId = useId();
    const errorId = useId();
    const today = myToday();
    const inputRef = useRef<HTMLInputElement>(null);
    const changeId = useId();
    const [open, setOpen] = useState(false);
    const [draft, setDraft] = useState(doc.replyBy ?? "");
    const [busy, setBusy] = useState<"save" | "reset" | null>(null);
    const [error, setError] = useState<string | null>(null);
    const wasOpen = useRef(false);

    // Opened: the cursor goes in the field. Folded: back to "Change".
    useEffect(() => {
        if (open && !wasOpen.current) inputRef.current?.focus();
        if (!open && wasOpen.current) document.getElementById(changeId)?.focus();
        wasOpen.current = open;
    }, [open, changeId]);

    const fold = () => {
        if (busy) return;
        setOpen(false);
        setError(null);
        setDraft(doc.replyBy ?? "");
    };
    useEscapeLayer(fold, open);

    const send = async (value: string | null) => {
        if (busy) return;
        if (value !== null) {
            if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) {
                setError("Pick a date.");
                inputRef.current?.focus();
                return;
            }
            if (value < today) {
                setError("Pick today or a later date.");
                inputRef.current?.focus();
                return;
            }
        }
        setBusy(value === null ? "reset" : "save");
        onBusy?.(true);
        setError(null);
        const r = await setDocumentReplyBy(doc.id, value);
        setBusy(null);
        onBusy?.(false);
        if (!r.ok) {
            setError(r.reason === "network" ? "The date did not save. Try again." : docsFailureText(r, "this document"));
            inputRef.current?.focus();
            return;
        }
        setDraft(r.data.replyBy ?? "");
        setOpen(false);
        const d = docDate(r.data.replyBy);
        onSaved(value === null ? (d ? `Reply-by is back to the default, ${d}.` : "Reply-by is back to the default.") : d ? `Reply by ${d}.` : "Reply-by saved.");
    };

    if (!open) {
        return (
            <PillButton
                id={changeId}
                variant="text"
                className="ml-auto"
                aria-label="Change the reply-by date"
                onClick={() => {
                    setDraft(doc.replyBy ?? "");
                    setOpen(true);
                }}
            >
                Change
            </PillButton>
        );
    }

    return (
        <form
            noValidate
            className="mt-2 w-full basis-full"
            onSubmit={(e) => {
                e.preventDefault();
                void send(draft);
            }}
        >
            <label htmlFor={fieldId} className="sr-only">
                Reply-by date for this doc
            </label>
            <div className="flex flex-wrap items-center gap-2">
                <input
                    ref={inputRef}
                    id={fieldId}
                    type="date"
                    min={today}
                    value={draft}
                    aria-invalid={error ? true : undefined}
                    aria-describedby={error ? errorId : undefined}
                    onChange={(e) => {
                        setDraft(e.target.value);
                        if (error) setError(null);
                    }}
                    className={FIELD}
                />
                <PillButton type="submit" size="sm" variant="primary" busy={busy === "save"} disabled={!draft || draft === doc.replyBy || busy !== null} className={TAP_TARGET}>
                    Save
                </PillButton>
                {doc.replyBySource === "staff" && (
                    <PillButton variant="text" busy={busy === "reset"} disabled={busy !== null} onClick={() => void send(null)}>
                        Back to the default
                    </PillButton>
                )}
                <PillButton variant="text" disabled={busy !== null} onClick={fold}>
                    Cancel
                </PillButton>
            </div>
            {error && (
                <p id={errorId} role="alert" className={cn(TYPE.tc, LATE_TEXT, "m-0 mt-2")}>
                    {error}
                </p>
            )}
        </form>
    );
}
